import type { Supplement } from '@/domain/Supplement';
import { api } from '@/lib/api';
import { mutationOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mutationKeys } from '../mutationKeys';

export interface IngredientFormData {
  id?: number;
  name: string;
  manufactor?: string;
  description?: string;
  source?: string;
  function_name?: string;
  unrestricted?: boolean;
  strengths?: Array<{ strength: string; price: number }>;
  image?: File;
  // New fields from rich content structure
  location?: string;
  found_in?: string;
  form?: string;
  supplier?: string;
  benefits_textarea?: string;
  doses?: string;
  product_name?: string;
  product_code?: string;
  review_date?: string;
  lot?: string;
  labaratory?: string;
  label_claim?: string;
  result?: string;
  supplement_packaging?: string;
  table_items_names?: string;
  first_disclaimer?: string;
  lab_results_numbers?: string;
  second_disclaimer?: string;
  articles?: Array<{ source: string; description: string }>;
  benefit_ids?: Array<number>;
}

export const createIngredientMutation = () => {
  return mutationOptions({
    mutationKey: mutationKeys.adminIngredients.create(),
    mutationFn: async (data: IngredientFormData) => {
      const formData = new FormData();
      formData.append('name', data.name);
      if (data.location) formData.append('location', data.location);
      if (data.function_name) formData.append('function', data.function_name); // Mapping function_name to function

      const content: Record<string, unknown> = {
        found_in: data.found_in,
        form: data.form,
        source: data.source,
        supplier: data.supplier,
        location: data.location,
        function: data.function_name,
        benefits_textarea: data.benefits_textarea,
        doses: data.doses,
        product_name: data.product_name,
        product_code: data.product_code,
        review_date: data.review_date,
        lot: data.lot,
        labaratory: data.labaratory,
        label_claim: data.label_claim,
        result: data.result,
        description: data.description,
        supplement_packaging: data.supplement_packaging,
        table_items_names: data.table_items_names,
        first_disclaimer: data.first_disclaimer,
        lab_results_numbers: data.lab_results_numbers,
        second_disclaimer: data.second_disclaimer,
        articles: data.articles,
      };

      formData.append('content', JSON.stringify(content));

      if (data.benefit_ids) {
        data.benefit_ids.forEach((id) =>
          formData.append('benefit_ids[]', id.toString()),
        );
      }

      if (data.unrestricted !== undefined)
        formData.append('unrestricted', data.unrestricted ? '1' : '0');

      if (data.image) {
        formData.append('image', data.image);
      }

      return await api
        .post('admin/ingredients', {
          body: formData,
          headers: {
            'Content-Type': undefined,
          },
        })
        .json<Supplement>();
    },
    onSuccess: () => {
      toast.success('Supplement created successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create supplement');
    },
  });
};

export const updateIngredientMutation = () => {
  return mutationOptions({
    mutationKey: mutationKeys.adminIngredients.update(),
    mutationFn: async (data: IngredientFormData) => {
      const formData = new FormData();
      formData.append('name', data.name);
      if (data.location) formData.append('location', data.location);
      if (data.function_name) formData.append('function', data.function_name);

      const content: Record<string, unknown> = {
        found_in: data.found_in,
        form: data.form,
        source: data.source,
        supplier: data.supplier,
        location: data.location,
        function: data.function_name,
        benefits_textarea: data.benefits_textarea,
        doses: data.doses,
        product_name: data.product_name,
        product_code: data.product_code,
        review_date: data.review_date,
        lot: data.lot,
        labaratory: data.labaratory,
        label_claim: data.label_claim,
        result: data.result,
        description: data.description,
        supplement_packaging: data.supplement_packaging,
        table_items_names: data.table_items_names,
        first_disclaimer: data.first_disclaimer,
        lab_results_numbers: data.lab_results_numbers,
        second_disclaimer: data.second_disclaimer,
        articles: data.articles,
      };

      formData.append('content', JSON.stringify(content));

      if (data.benefit_ids) {
        data.benefit_ids.forEach((id) =>
          formData.append('benefit_ids[]', id.toString()),
        );
      }

      if (data.unrestricted !== undefined)
        formData.append('unrestricted', data.unrestricted ? '1' : '0');

      if (data.image) {
        formData.append('image', data.image);
      }

      formData.append('_method', 'PUT');

      return await api
        .post(`admin/ingredients/${data.id}`, {
          body: formData,
          headers: {
            'Content-Type': undefined,
          },
        })
        .json<Supplement>();
    },
    onSuccess: () => {
      toast.success('Supplement updated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update supplement');
    },
  });
};

export const deleteIngredientMutation = () => {
  return mutationOptions({
    mutationKey: mutationKeys.adminIngredients.delete(),
    mutationFn: async (id: number) => {
      return await api.delete(`admin/ingredients/${id}`).json<void>();
    },
    onSuccess: () => {
      toast.success('Supplement deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete supplement');
    },
  });
};
